import React from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './components/Layout/Layout'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import Users from './pages/Users'
import SASUsers from './pages/SASUsers'
import Organizations from './pages/Organizations'
import OrganizationDetail from './pages/OrganizationDetail'
import EditOrganization from './pages/EditOrganization'
import Products from './pages/Products'
import ProductDetail from './pages/ProductDetail'
import EditProduct from './pages/EditProduct'
import Roles from './pages/Roles'
import Profile from './pages/Profile'
import AcceptInvitation from './pages/AcceptInvitation'
import Invoice from './pages/Invoice'
import HubFeatures from './pages/HubFeatures'
import Integration from './pages/Integration'
import Reports from './pages/Reports'
import DataBackup from './pages/DataBackup'
import Branding from './pages/Branding'
import HubBrandingApplier from './components/Layout/HubBrandingApplier'
import LoadingSpinner from './components/UI/LoadingSpinner'

function AppRoutes() {
  const { user, loading } = useAuth()
  const location = useLocation()
  const isInvitationRoute = location.pathname.startsWith('/accept-invitation/')

  // Check if SSO redirect is in progress (don't redirect to login while SSO is processing)
  // Also check if we're on login page (allow SSO processing to complete there)
  const [isSSORedirectInProgress, setIsSSORedirectInProgress] = React.useState(false)
  
  // Clear SSO markers if they're stuck (older than 30 seconds) - likely a failed redirect
  // Check periodically to prevent infinite spinner
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    
    const checkSSOMarkers = () => {
      const ssoEmail = sessionStorage.getItem('sso_email')
      const ssoTenantId = sessionStorage.getItem('sso_tenant_id')
      const ssoTimestamp = sessionStorage.getItem('sso_timestamp')
      const linkAzureAfterPassword = sessionStorage.getItem('link_azure_after_password')
      
      // "Password first then link Azure" flow: we have sso_tenant_id but no redirect - don't show spinner or clear
      const isPasswordFirstFlow = linkAzureAfterPassword === 'true'
      
      // Check if SSO redirect is in progress (not when we're showing password first)
      const hasSSOMarkers = !!(ssoEmail || ssoTenantId)
      const isOnLoginPage = location.pathname === '/login'
      setIsSSORedirectInProgress(hasSSOMarkers && isOnLoginPage && !isPasswordFirstFlow)
      
      // Clear stale markers (older than 2 minutes for redirects, 30s for popups) - but never clear when in password-first flow
      // Redirects can take longer (user has to authenticate), so give them more time
      if (hasSSOMarkers && ssoTimestamp && !isPasswordFirstFlow) {
        const timestamp = parseInt(ssoTimestamp, 10)
        const now = Date.now()
        const elapsed = now - timestamp
        
        // Check if we're coming back from a redirect (referrer includes firebaseapp.com or microsoftonline.com)
        const isFromRedirect = document.referrer.includes('firebaseapp.com') || 
                               document.referrer.includes('microsoftonline.com') ||
                               document.referrer.includes('__/auth/handler')
        
        // For redirects, allow up to 2 minutes (120 seconds) before clearing
        // For popups, use 30 seconds
        const timeout = isFromRedirect ? 120000 : 30000
        
        if (elapsed > timeout) {
          console.warn(`[App] Clearing stale SSO markers (older than ${timeout/1000}s)`, {
            elapsed: `${Math.round(elapsed/1000)}s`,
            isFromRedirect,
            referrer: document.referrer
          })
          sessionStorage.removeItem('sso_email')
          sessionStorage.removeItem('sso_tenant_id')
          sessionStorage.removeItem('sso_config')
          sessionStorage.removeItem('sso_timestamp')
          setIsSSORedirectInProgress(false)
        }
      }
    }
    
    // Check immediately
    checkSSOMarkers()
    
    // Check every 2 seconds to update state and clear stale markers
    const interval = setInterval(checkSSOMarkers, 2000)
    
    return () => clearInterval(interval)
  }, [location.pathname])

  // Show spinner only if:
  // 1. AuthContext is loading (initial auth check)
  // 2. SSO redirect is in progress AND user is not authenticated yet
  // But limit SSO spinner to max 10 seconds to prevent infinite spinner
  const [ssospinnerTimeout, setSSOSpinnerTimeout] = React.useState(false)
  
  React.useEffect(() => {
    if (isSSORedirectInProgress && !user) {
      // Set timeout to stop spinner after 10 seconds (only clear markers if not in password-first flow)
      const timeout = setTimeout(() => {
        const linkAzureAfterPassword = sessionStorage.getItem('link_azure_after_password')
        if (linkAzureAfterPassword === 'true') {
          // Password-first flow: stop spinner but keep markers so Login stays on password step
          setSSOSpinnerTimeout(true)
          return
        }
        console.warn('[App] SSO spinner timeout - clearing markers and stopping spinner')
        setSSOSpinnerTimeout(true)
        sessionStorage.removeItem('sso_email')
        sessionStorage.removeItem('sso_tenant_id')
        sessionStorage.removeItem('sso_config')
        sessionStorage.removeItem('sso_timestamp')
      }, 10000) // 10 seconds max
      
      return () => clearTimeout(timeout)
    } else {
      setSSOSpinnerTimeout(false)
    }
  }, [isSSORedirectInProgress, user])
  
  const shouldShowSpinner = loading || (isSSORedirectInProgress && !user && !ssospinnerTimeout)
  
  if (shouldShowSpinner) {
    // Show loading while SSO redirect is being processed
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // If user is logged in and on invitation route, show invitation page without Layout
  if (isInvitationRoute) {
    return (
      <Routes>
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
      </Routes>
    )
  }

  // All other authenticated routes with Layout
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/users" element={<Users />} />
        <Route path="/sas-users" element={<SASUsers />} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/organizations/:id" element={<OrganizationDetail />} />
        <Route path="/organizations/:id/edit" element={<EditOrganization />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/products/:id/edit" element={<EditProduct />} />
        <Route path="/roles" element={<Roles />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/invoice" element={<Invoice />} />
        <Route path="/hub-features" element={<HubFeatures />} />
        <Route path="/integration" element={<Integration />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/data-backup" element={<DataBackup />} />
        <Route path="/branding" element={<Branding />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  )
}

function App() {
  return (
    <AuthProvider>
      <HubBrandingApplier />
      <div className="min-h-screen bg-secondary-50">
        <AppRoutes />
      </div>
    </AuthProvider>
  )
}

export default App




